/**
 * Send people who type one of the plugin domains into a browser to that
 * plugin's Figma Community page.
 *
 * These domains exist to serve the plugin UI to Figma, not to be read by a
 * human: opening illustrator.plugin.tomake.design directly gets you a panel stuck on
 * "Connecting to Figma…", because the sandbox that would hand it a document
 * isn't there. The page a person actually wants is the Community listing, where
 * they can install it.
 *
 * The catch is that both audiences load the identical URL — Figma frames it, a
 * visitor navigates to it — so the redirect has to tell them apart, and it has
 * to be right, because getting it wrong means the plugin redirects itself out
 * of existence mid-import. Framing is the signal: a document nested in a frame
 * is a plugin UI, full stop. It's a structural fact rather than a hint like a
 * request header, and the failure mode is the safe one — anything we can't
 * confidently call a direct visit stays put and renders the UI.
 *
 * (`psd.plugin.tomake.design` solves this in its own app with a `_redirects` rule
 * instead. It can: Figma never frames it — that plugin bundles its UI and only
 * ever *fetches* the domain, for telemetry — so there is no plugin to break.)
 */

import { VARIANTS } from './variants';
import { isPublished } from '@figma-importers/variants';

/** The Figma Community listing for a published plugin id. */
export function communityUrl(id: string): string {
  return `https://www.figma.com/community/plugin/${id}`;
}

/**
 * The Community page for whichever plugin is served from `host`, or `undefined`
 * when the host isn't one of ours — a preview deployment, a `pages.dev` URL, or
 * localhost, none of which should bounce a developer away mid-session.
 */
export function communityUrlForHost(host: string): string | undefined {
  // A variant that is not yet in the Community has nowhere to send a visitor.
  const variant = VARIANTS.find((v) => isPublished(v) && new URL(v.ui).host === host);
  return variant && communityUrl(variant.id);
}

/** The bits of `window` this needs, so tests can hand it a plausible fake. */
export interface WindowLike {
  self: unknown;
  top: unknown;
  location: { hostname: string; replace(url: string): void };
}

/** Whether this document is nested inside a frame — i.e. it is a plugin UI. */
export function isFramed(win: WindowLike): boolean {
  try {
    return win.self !== win.top;
  } catch {
    // Touching `top` across origins throws, and that throw is itself proof of
    // framing: a top-level document is its own top and never trips the check.
    return true;
  }
}

/**
 * Redirect, if this really is someone visiting the domain directly. Returns the
 * URL it sent them to, or `undefined` if it left them alone — which is what the
 * tests assert on, since a jsdom-free `location.replace` isn't observable.
 */
export function redirectIfVisitedDirectly(win: WindowLike): string | undefined {
  if (isFramed(win)) return undefined;
  const target = communityUrlForHost(win.location.hostname);
  if (target) win.location.replace(target);
  return target;
}
